// ─────────────────────────────────────────────────────────────────────────────
// AddAliasComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dialog for creating and editing product aliases.
// Uses <app-shared-dialog> + mat-autocomplete for product/customer search.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { IProductAlias, ICreateAliasPayload, IUpdateAliasPayload } from '../../models/alias.models';
import { CreateAlias, CreateAliasSuccess, UpdateAlias, UpdateAliasSuccess } from '../../store/aliases.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

interface SelectOption {
  value: number;
  label: string;
  sku?: string;
}

@Component({
  selector: 'app-add-alias',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, UiModule, SharedDialogComponent],
  templateUrl: './add-alias.component.html',
  styleUrls: ['./add-alias.component.scss'],
})
export class AddAliasComponent implements OnInit, OnDestroy {
  // ── Form ──────────────────────────────────────────────────────────────────

  form!: FormGroup;
  productSearchControl = new FormControl('');
  customerSearchControl = new FormControl('');

  // ── State ─────────────────────────────────────────────────────────────────

  isUpdate = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);

  // ── Autocomplete results ──────────────────────────────────────────────────

  filteredProducts$!: Observable<SelectOption[]>;
  filteredCustomers$!: Observable<SelectOption[]>;
  aliasTypeOptions: { value: string; label: string }[] = [];
  productSearchState: 'idle' | 'searching' | 'results' = 'idle';
  customerSearchState: 'idle' | 'searching' | 'results' = 'idle';

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AddAliasComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IProductAlias | null,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService,
    private productsService: ProductsService,
    private customersService: CustomersService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initForm();
    this.initAliasTypeOptions();
    this.initProductAutocomplete();
    this.initCustomerAutocomplete();
    this.initClearDetection();
    this.handleEditMode();

    this.canSubmit$ = this.store.select(selectCanDo('knowledge_base', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      if (canSubmit) {
        this.form.enable({ emitEvent: false });
        this.productSearchControl.enable({ emitEvent: false });
        this.customerSearchControl.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
        this.productSearchControl.disable({ emitEvent: false });
        this.customerSearchControl.disable({ emitEvent: false });
      }
    });

    this.listenForSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Initialization ────────────────────────────────────────────────────────

  private initForm(): void {
    this.form = this.fb.group({
      sku: ['', Validators.required],
      alias_value: ['', Validators.required],
      alias_type: ['name'],
      customer_id: [null],
    });
  }

  private initAliasTypeOptions(): void {
    this.aliasTypeOptions = [
      { value: 'name', label: this.translateService.instant('knowledge_base.aliases.type.name') },
      { value: 'code', label: this.translateService.instant('knowledge_base.aliases.type.code') },
      { value: 'description', label: this.translateService.instant('knowledge_base.aliases.type.description') },
    ];
  }

  private initProductAutocomplete(): void {
    this.filteredProducts$ = this.productSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.productSearchState = 'idle';
          return of([]);
        }
        this.productSearchState = 'searching';
        return this.productsService.fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, keyword, undefined, false, true).pipe(
          map((res) => {
            const options = (res.data ?? [])
              .filter((p) => p.id != null && p.sku)
              .map((p) => ({
                value: p.id!,
                label: `${p.name} (${p.sku})`,
                sku: p.sku,
              }));
            this.productSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initCustomerAutocomplete(): void {
    this.filteredCustomers$ = this.customerSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.customerSearchState = 'idle';
          return of([]);
        }
        this.customerSearchState = 'searching';
        return this.customersService.listCustomers({ number: 1, size: 20 }, undefined, keyword).pipe(
          map((res) => {
            const options = (res.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }));
            this.customerSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initClearDetection(): void {
    this.productSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ sku: '' });
      }
    });

    this.customerSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ customer_id: null });
      }
    });
  }

  private handleEditMode(): void {
    if (!this.data) return;

    this.isUpdate = true;

    this.form.patchValue({
      sku: this.data.sku,
      alias_value: this.data.alias_value,
      alias_type: this.data.alias_type,
      customer_id: this.data.customer_id,
    });

    // Set display text for autocomplete inputs
    if (this.data.product_name) {
      const label = `${this.data.product_name}${this.data.sku ? ' (' + this.data.sku + ')' : ''}`;
      this.productSearchControl.setValue({ value: this.data.product_id, label, sku: this.data.sku } as any);
    }
    if (this.data.customer_name) {
      this.customerSearchControl.setValue({ value: this.data.customer_id, label: this.data.customer_name } as any);
    }
  }

  private listenForSuccess(): void {
    this.actions$.pipe(ofType(CreateAliasSuccess, UpdateAliasSuccess), take(1), takeUntil(this.destroy$)).subscribe(() => this.close());
  }

  // ── Autocomplete handlers ─────────────────────────────────────────────────

  onProductSelected(option: SelectOption): void {
    this.form.patchValue({ sku: option.sku });
  }

  onCustomerSelected(option: SelectOption): void {
    this.form.patchValue({ customer_id: option.value });
  }

  clearProduct(): void {
    this.productSearchControl.setValue('');
    this.form.patchValue({ sku: '' });
  }

  clearCustomer(): void {
    this.customerSearchControl.setValue('');
    this.form.patchValue({ customer_id: null });
  }

  displayProductFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  displayCustomerFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  submitPermission() {
    return { module: 'knowledge_base', action: this.isUpdate ? 'edit' : 'create' };
  }

  save(): void {
    if (!this.canSubmit) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { sku, alias_value, alias_type, customer_id } = this.form.getRawValue();

    if (this.isUpdate && this.data) {
      const payload: IUpdateAliasPayload = {
        sku,
        alias_value,
        alias_type,
      };
      if (customer_id != null) {
        payload.customer_id = customer_id;
      }
      this.store.dispatch(UpdateAlias({ id: this.data.id, payload }));
    } else {
      const payload: ICreateAliasPayload = { sku, alias_value, alias_type };
      if (customer_id != null) {
        payload.customer_id = customer_id;
      }
      this.store.dispatch(CreateAlias({ payload }));
    }
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close();
  }
}
